import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Badge, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FiPlus, FiCheck, FiX, FiEdit2, FiTrash2, FiFilter } from 'react-icons/fi';
import { toast } from 'react-toastify';
import tasksService, { Task } from '../../services/tasks.service';

const TasksList: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    semaphore: ''
  });

  useEffect(() => {
    loadTasks();
  }, [filters]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const result = await tasksService.list({
        status: filters.status || undefined,
        priority: filters.priority || undefined,
        semaphore: filters.semaphore || undefined
      });
      setTasks(result.data);
    } catch (error) {
      toast.error('Error al cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (id: number) => {
    try {
      await tasksService.complete(id);
      toast.success('Tarea completada');
      loadTasks();
    } catch (error) {
      toast.error('Error al completar la tarea');
    }
  };

  const handleCancel = async (id: number) => {
    try {
      await tasksService.cancel(id);
      toast.success('Tarea cancelada');
      loadTasks();
    } catch (error) {
      toast.error('Error al cancelar la tarea');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Eliminar esta tarea?')) {
      try {
        await tasksService.delete(id);
        toast.success('Tarea eliminada');
        loadTasks();
      } catch (error) {
        toast.error('Error al eliminar la tarea');
      }
    }
  };

  const getSemaphoreBadge = (color: string) => {
    const colors: Record<string, string> = {
      green: 'success',
      yellow: 'warning',
      red: 'danger'
    };
    return <Badge bg={colors[color] || 'secondary'}>●</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const variants: Record<string, string> = {
      urgent: 'danger',
      high: 'warning',
      normal: 'info',
      low: 'secondary'
    };
    const labels: Record<string, string> = {
      urgent: 'Urgente',
      high: 'Alta',
      normal: 'Normal',
      low: 'Baja'
    };
    return <Badge bg={variants[priority] || 'secondary'}>{labels[priority] || priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
      cancelled: 'secondary'
    };
    const labels: Record<string, string> = {
      pending: 'Pendiente',
      in_progress: 'En Progreso',
      completed: 'Completada',
      cancelled: 'Cancelada'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO');
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">Tareas</h1>
        <Link to="/tasks/new" className="btn btn-primary">
          <FiPlus className="me-2" /> Nueva Tarea
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label><FiFilter className="me-1" /> Estado</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="pending">Pendiente</option>
                <option value="in_progress">En Progreso</option>
                <option value="completed">Completada</option>
                <option value="cancelled">Cancelada</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Prioridad</Form.Label>
              <Form.Select
                value={filters.priority}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              >
                <option value="">Todas</option>
                <option value="urgent">Urgente</option>
                <option value="high">Alta</option>
                <option value="normal">Normal</option>
                <option value="low">Baja</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Semáforo</Form.Label>
              <Form.Select
                value={filters.semaphore}
                onChange={(e) => setFilters({ ...filters, semaphore: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="red">🔴 Vencido</option>
                <option value="yellow">🟡 Por vencer</option>
                <option value="green">🟢 A tiempo</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button variant="outline-secondary" onClick={() => setFilters({ status: '', priority: '', semaphore: '' })}>
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No hay tareas registradas
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '30px' }}></th>
                  <th>Tarea</th>
                  <th>Caso</th>
                  <th>Prioridad</th>
                  <th>Estado</th>
                  <th>Vencimiento</th>
                  <th>Asignado</th>
                  <th style={{ width: '120px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td className="text-center">{getSemaphoreBadge(task.semaphore)}</td>
                    <td>
                      <strong>{task.title}</strong>
                      {task.description && (
                        <div className="text-muted small">{task.description.substring(0, 50)}...</div>
                      )}
                    </td>
                    <td>
                      {task.case_number ? (
                        <Link to={`/cases/${task.case_id}`}>{task.case_number}</Link>
                      ) : '-'}
                    </td>
                    <td>{getPriorityBadge(task.priority)}</td>
                    <td>{getStatusBadge(task.status)}</td>
                    <td>{formatDate(task.due_date)}</td>
                    <td>{task.assigned_to_name || '-'}</td>
                    <td>
                      {task.status === 'pending' || task.status === 'in_progress' ? (
                        <>
                          <Button
                            variant="outline-success"
                            size="sm"
                            className="me-1"
                            title="Completar"
                            onClick={() => handleComplete(task.id)}
                          >
                            <FiCheck />
                          </Button>
                          <Link to={`/tasks/${task.id}/edit`} className="btn btn-outline-primary btn-sm me-1" title="Editar">
                            <FiEdit2 />
                          </Link>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            title="Cancelar"
                            onClick={() => handleCancel(task.id)}
                          >
                            <FiX />
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Eliminar"
                          onClick={() => handleDelete(task.id)}
                        >
                          <FiTrash2 />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default TasksList;
